import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps, InputLabel } from '@material-ui/core';
import { useStyles } from '../../../pages/styles';
import { useStyles as myStyles } from './FaqEditor.styles';
import { LeavePrompt } from './../../Prompt/LeavePrompt/LeavePrompt.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import ReactQuill from 'react-quill';
import { sanitizeHtmlContent, modules } from '../../../utils/reactQuillUtils'

const NAME_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 1000;

interface FaqEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (faqId: string, question: string, answer: string) => void;
  onChange: () => void;
  question: string;
  answer: string;
  faqId: string;
}

export const FaqEditor: React.FC<FaqEditorProps> = ({
  title,
  onCancel,
  onSave,
  question: questionProp,
  answer: answerProp,
  faqId: faqIdProp,
}) => {
  const classes = myStyles();
  const globalClasses = useStyles();
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [question, setQuestion] = useState<string>(questionProp ?? '');
  const [hasNameFocus, setHasNameFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const [htmlContent, setHtmlContent] = useState<string>(answerProp ?? '');
  const [rawHtmlContent, setRawHtmlContent] = useState<string>(answerProp ?? '');
  const [isHtmlEditor, setIsHtmlEditor] = useState<boolean>(true);

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
  };


  useEffect(() => {
    setIsDataChanged(questionProp !== question || htmlContent !== answerProp);
  }, [questionProp, question, htmlContent, answerProp]);

  const isQuestionTooLong = question.length > NAME_MAX_LENGTH;
  const isAnswerTooLong = htmlContent.length > DESCRIPTION_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  const handleSaveRawHtmlContent = (htmlContent: string) => {
    setRawHtmlContent(htmlContent);
    setHtmlContent(htmlContent);
  }

  const switchHtmlEditor = () => {
    setHasDescriptionFocus(false);
    isHtmlEditor ? setRawHtmlContent(htmlContent) : setHtmlContent(rawHtmlContent);
    setIsHtmlEditor(!isHtmlEditor);
  }
  
  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="md"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(faqIdProp, question, htmlContent)}
        confirmButtonDisabled={
          !question ||
          !htmlContent ||
          isQuestionTooLong ||
          isAnswerTooLong ||
          !isDataChanged
        }
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={question}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleQuestionChange(e.target.value)}
              label="Pytanie*"
              variant="outlined"
              fullWidth
              onFocus={() => setHasNameFocus(true)}
              onBlur={() => setHasNameFocus(false)}
              error={isQuestionTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isQuestionTooLong ? 'Podane pytanie jest za długie' : ''
                  }
                  limitsVisible={hasNameFocus || isQuestionTooLong}
                  valueLength={question.length}
                  limit={NAME_MAX_LENGTH}
                  withError={isQuestionTooLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
                <div style={{ textAlign: 'left' }}>
                  <ButtonOne
                    className={globalClasses.primary}
                    style={{ marginBottom: 15 }}
                    variant="contained"
                    aria-label="Edytuj"
                    onClick={switchHtmlEditor}
                  >{isHtmlEditor ? 'Kod HTML' : 'Edytor HTML'}</ButtonOne>
                </div>
                
                {isHtmlEditor && <>
                  <ReactQuill 
                    placeholder= "Odpowiedź*" 
                    theme="snow" 
                    className={globalClasses.reactQuill} 
                    value={htmlContent} 
                    onChange={setHtmlContent} 
                    modules={modules}
                    onFocus={() => setHasDescriptionFocus(true)}
                    onBlur={() => setHasDescriptionFocus(false)} />
                </>}

                {!isHtmlEditor && <TextField
                  value={rawHtmlContent}
                  multiline
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleSaveRawHtmlContent(e.target.value)}
                  onFocus={() => setHasDescriptionFocus(true)}
                  onBlur={() => setHasDescriptionFocus(false)}
                  label="Odpowiedź*"
                  variant="outlined"
                  fullWidth
                  minRows="7"
                  maxRows="15"
                  FormHelperTextProps={
                    {
                      component: 'div',
                    } as Partial<FormHelperTextProps<'p'>>
                  }
                />}
              </Grid>
        </Grid>
        {!isHtmlEditor && <div className={classes.item}>
              <h3>Podgląd HTML:</h3>
              {<div className={`ql-editor ${classes.showHtml}`} dangerouslySetInnerHTML={{ __html: sanitizeHtmlContent(rawHtmlContent) }} />}
            </div>}
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
